import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import CustomButton from '../../../components/CustomButton';
import {
    useCreateCodeDoorMutation,
    useGetAvailableCodeDoorAssetsQuery,
    useUpdateCodeDoorCodeMutation,
} from '../../../api/accessCodeDoor/accessCodeDoorApiSlice';
import { CodeDoor } from '../../../api/accessCodeDoor/accessCodeDoorApiTypes';
import { formatCodeDoorLabel } from './codeDoorLabel';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../../action/snackbarCommands';
import { errorMessageFrom } from '../../../utils/errorMessage';

interface CodeDoorFormDialogProps {
    onClose: () => void;
    /** When set, the dialog changes this door's code instead of creating one
     *  (the asset link is immutable — 1:1, remove and re-create to move it). */
    door?: CodeDoor;
    /** Create for THIS object, skipping the picker — the object detail page
     *  already knows which object it is (ADR-0001 Amendment 3 moved the
     *  surface there). Ignored when `door` is set. */
    fixedAsset?: { id: string, label: string };
}


/**
 * Create / edit dialog for a code door (`static_code` access point). Create
 * either picks an eligible asset (backend-owned list — only assets without an
 * access point, plan A4) or is opened for a fixed object; edit changes the code
 * only. The code is entered in plain text on purpose: the admin needs to read
 * what they type, and it must match what is physically programmed on the lock.
 */
const CodeDoorFormDialog = ({ onClose, door, fixedAsset }: CodeDoorFormDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isEdit = !!door;
    // The picker only exists for the free-choice create path; with a fixed
    // object there is nothing to choose, so the eligibility list is not fetched.
    const usesPicker = !isEdit && !fixedAsset;

    const { data: availableAssets, isLoading: assetsLoading } =
        useGetAvailableCodeDoorAssetsQuery(undefined, { skip: !usesPicker });
    const [createCodeDoor, { isLoading: creating }] = useCreateCodeDoorMutation();
    const [updateCodeDoorCode, { isLoading: updating }] = useUpdateCodeDoorCodeMutation();
    const saving = creating || updating;

    const [assetId, setAssetId] = useState('');
    const [code, setCode] = useState('');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const assetOptions = useMemo(
        () => (availableAssets ?? []).map((asset) => asset.id),
        [availableAssets],
    );
    const assetLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        (availableAssets ?? []).forEach((asset) => {
            labels[asset.id] = formatCodeDoorLabel(asset.name, asset.locationName, asset.id);
        });
        return labels;
    }, [availableAssets]);

    // Which object the dialog is for: the door's when editing, the page's when
    // creating for a fixed object. Resolved here rather than in the markup —
    // a ternary inside a template literal inside a ternary is not readable.
    const objectLabel = door
        ? formatCodeDoorLabel(door.assetName, door.locationName, door.assetId)
        : fixedAsset?.label ?? '';

    const targetAssetId = fixedAsset ? fixedAsset.id : assetId;
    const assetError = touched && usesPicker && !assetId;
    const codeError = touched && !code.trim();
    const canSubmit = !!code.trim() && (isEdit || !!targetAssetId);

    const handleSubmit = async () => {
        setTouched(true);
        if (!canSubmit) return;
        try {
            if (isEdit && door) {
                await updateCodeDoorCode({ id: door.id, code: code.trim() }).unwrap();
                dispatch(enqueueSuccessSnackbar({
                    message: t('code_doors.edit_success'),
                    autoHideDuration: 3000,
                }));
            } else {
                await createCodeDoor({ assetId: targetAssetId, code: code.trim() }).unwrap();
                dispatch(enqueueSuccessSnackbar({
                    message: t('code_doors.create_success'),
                    autoHideDuration: 3000,
                }));
            }
            onClose();
        } catch (err) {
            // 409 is the 1:1 guard (plan A4) and it is the one failure an
            // operator can act on — the object already carries an access
            // point, typically an electronic lock. It gets its own message
            // instead of the backend's English string.
            const conflict = (err as { status?: number }).status === 409;
            const fallback = t(isEdit ? 'code_doors.edit_error' : 'code_doors.create_error');
            const message = conflict
                ? t('code_doors.create_conflict')
                : errorMessageFrom(err, fallback, t);
            dispatch(enqueueErrorSnackbar({ message }));
        }
    };

    return createPortal(
        <div className={'fixed inset-0 z-[1300] flex items-center justify-center p-4'}>
            {/* Decorative click-to-close backdrop — keyboard users close via the
                Escape handler above, so it's hidden from assistive tech (S1082). */}
            <div
                className={'fixed inset-0 bg-black bg-opacity-50'}
                onClick={onClose}
                aria-hidden={'true'}
            />

            <div
                className={'relative z-10 bg-text-contrast rounded-lg shadow-xl w-full max-w-md'}
                role={'dialog'}
                aria-modal={'true'}
            >
                <div className={'p-6'}>
                    <h3 className={'text-lg font-semibold text-foreground mb-1'}>
                        {isEdit
                            ? t('code_doors.edit_title')
                            : t('code_doors.create_title')}
                    </h3>
                    <p className={'text-sm text-foreground/60 mb-5'}>
                        {isEdit
                            ? t('code_doors.edit_description')
                            : t('code_doors.create_description')}
                    </p>

                    <div className={'flex flex-col gap-4'}>
                        {!usesPicker ? (
                            <div>
                                <p className={'text-sm text-foreground/60'}>
                                    {t('code_doors.asset_label')}
                                </p>
                                <p className={'text-[15px] font-medium text-foreground'}>
                                    {objectLabel}
                                </p>
                            </div>
                        ) : (
                            <SelectField
                                label={t('code_doors.asset_label')}
                                value={assetId}
                                onChange={setAssetId}
                                options={assetOptions}
                                optionLabels={assetLabels}
                                loading={assetsLoading}
                                noOptions={t('code_doors.no_assets_available')}
                                error={assetError}
                                labelAbove
                            />
                        )}
                        <InputField
                            label={t('code_doors.code_label')}
                            value={code}
                            onChange={setCode}
                            placeholder={t('code_doors.code_placeholder')}
                            error={codeError}
                            errorText={t('code_doors.code_required')}
                        />
                        <p className={'text-xs text-foreground/60'}>
                            {t('code_doors.code_hint')}
                        </p>
                    </div>
                </div>

                <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                    <CustomButton
                        title={t(isEdit ? 'code_doors.edit_cta' : 'code_doors.create_cta')}
                        onClick={handleSubmit}
                        loading={saving}
                        disabled={!canSubmit && touched}
                    />
                    <button
                        type={'button'}
                        className={'text-sm font-medium text-foreground/70 px-4'}
                        onClick={onClose}
                    >
                        {t('code_doors.cancel')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default CodeDoorFormDialog;
